/*
 * Amazon Ads reporting, in plain words: one vocabulary for both portals.
 *
 * Every Amazon surface reads the same backend object, readiness `reporting`:
 * AMAZON.reporting on GET /api/integrations/:advertiserId, and in Quantum also
 * platforms.amazon.reporting on GET /api/integrations/health and
 * readiness.reporting on GET /api/amazon/diagnostics. Its status is one of:
 *
 *   not_enabled            reporting is not switched on for this advertiser
 *   awaiting_first_import  switched on; no import on this connection yet.
 *                          The backend also reads it after a disconnect or a
 *                          failed authorization, when delivery imported
 *                          earlier stays stored, so it never means "nothing
 *                          was ever imported"
 *   imported_all_held      imports run, but every row so far waits for an
 *                          operator (for example an unbound Amazon campaign)
 *   imported_empty         imports run; Amazon reported no delivery
 *   imported               delivery has been stored
 *
 * Two rules every caller keeps:
 *  - Reporting is claimed only for `imported`. Any other state, an unknown
 *    state or a missing object never reads as current delivery.
 *  - `lastImportAt` is the last import. A connection check
 *    (`integrationLastCheckedAt`) is a different fact, always labelled apart.
 *    A missing time reads "—", never a stand-in time and never zero.
 *
 * Byte-identical in Quantum and Cortex (crossRepositoryParity pins it). The
 * client vocabulary says "awaiting review" where operators read "all held",
 * and it carries no reason: the reason names operator work.
 */

export const AMAZON_REPORTING_STATUSES = Object.freeze([
  "not_enabled",
  "awaiting_first_import",
  "imported_all_held",
  "imported_empty",
  "imported",
]);

export const AMAZON_REPORTING_STATUS_LABELS = Object.freeze({
  not_enabled: "Not enabled",
  awaiting_first_import: "Awaiting import",
  imported_all_held: "Imported — all held",
  imported_empty: "Imported — no delivery",
  imported: "Imported",
});

export const AMAZON_REPORTING_CLIENT_STATUS_LABELS = Object.freeze({
  ...AMAZON_REPORTING_STATUS_LABELS,
  imported_all_held: "Imported — awaiting review",
});

export const AMAZON_REPORTING_UNKNOWN_LABEL = "Unknown";
export const AMAZON_REPORTING_MISSING_VALUE = "—";

// good: delivery is stored. attention: rows wait for an operator. neutral:
// nothing is wrong, and nothing is reported either.
const STATUS_TONES = Object.freeze({
  not_enabled: "neutral",
  awaiting_first_import: "neutral",
  imported_all_held: "attention",
  imported_empty: "neutral",
  imported: "good",
});

const trimmed = (value) => (typeof value === "string" ? value.trim() : "");

const timestampOrNull = (value) => {
  const text = trimmed(value);
  return text && Number.isFinite(Date.parse(text)) ? text : null;
};

const dateOnlyOrNull = (value) => {
  const text = trimmed(value);
  return /^\d{4}-\d{2}-\d{2}$/.test(text) && Number.isFinite(Date.parse(`${text}T00:00:00Z`))
    ? text
    : null;
};

/*
 * The reporting object, read once. Null when the response carries none (a
 * backend without Amazon reporting), so a caller renders nothing rather than
 * a guess. `audience: "client"` reads the client vocabulary and drops the
 * reason.
 */
export function readAmazonReporting(reporting, { audience = "operator" } = {}) {
  if (!reporting || typeof reporting !== "object") return null;
  const raw = trimmed(reporting.status).toLowerCase();
  const status = AMAZON_REPORTING_STATUSES.includes(raw) ? raw : null;
  const labels =
    audience === "client" ? AMAZON_REPORTING_CLIENT_STATUS_LABELS : AMAZON_REPORTING_STATUS_LABELS;
  const lastImportAt = timestampOrNull(reporting.lastImportAt);
  const held = Number(reporting.heldAtLastImport);
  return {
    status,
    label: status ? labels[status] : AMAZON_REPORTING_UNKNOWN_LABEL,
    tone: status ? STATUS_TONES[status] : "neutral",
    // Delivery is stored, so reporting may be claimed. Nothing else is.
    reportingLive: status === "imported",
    lastImportAt,
    deliveryStoredAt: timestampOrNull(reporting.deliveryStoredAt),
    // A count of the last import's held rows: no import, no count.
    heldAtLastImport:
      lastImportAt && reporting.heldAtLastImport != null && Number.isInteger(held) && held >= 0
        ? held
        : null,
    dataThrough: dateOnlyOrNull(reporting.dataThrough),
    reason: audience === "client" ? null : trimmed(reporting.reason) || null,
  };
}

// A time from the reporting object, in the viewer's locale, or "—".
export function formatAmazonReportingTime(value) {
  const text = timestampOrNull(value);
  return text ? new Date(text).toLocaleString() : AMAZON_REPORTING_MISSING_VALUE;
}

// A reporting day (YYYY-MM-DD) as "Nov 2, 2026", or "—". Read in UTC, so the
// day never shifts with the viewer's time zone.
export function formatAmazonDataThrough(value) {
  const text = dateOnlyOrNull(value);
  if (!text) return AMAZON_REPORTING_MISSING_VALUE;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(`${text}T00:00:00Z`));
}

/*
 * The verdict Quantum shows while it has no diagnostics (a read that failed or
 * has not answered yet). It names no blocker, because none has been read.
 */
export const AMAZON_DIAGNOSTICS_UNAVAILABLE_VERDICT = "diagnostics_unavailable";

/*
 * The diagnostics verdict's tone (Quantum). `reporting_imported` is good.
 * Verified profile access whose reporting is not imported is neutral
 * (attention while rows wait for an operator). Having no diagnostics to read
 * is neutral too. Every other verdict names a blocker and keeps its warning
 * tone.
 */
export function amazonDiagnosticsVerdictTone(verdict) {
  const text = trimmed(verdict).toLowerCase();
  if (text === "reporting_imported") return "good";
  if (text === AMAZON_DIAGNOSTICS_UNAVAILABLE_VERDICT) return "neutral";
  if (text.startsWith("profile_access_verified_reporting_")) {
    return text === "profile_access_verified_reporting_imported_all_held" ? "attention" : "neutral";
  }
  return "blocked";
}
